import { clsx } from 'clsx';
import { Check, Copy } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { formatCountdown, humanize, isUuid, shortId } from '../format.ts';

export interface IdTextProps {
  value: string | undefined;
  /** Characters to show; the full value is in the title and copied. Default: full value. */
  length?: number;
  copy?: boolean;
  className?: string;
  label?: string;
}

/** Monospace id or hash with optional truncation and a copy button. */
export function IdText({ value, length, copy = false, className, label }: IdTextProps) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-ink-3">—</span>;
  const shown = length ? shortId(value, length) : value;
  return (
    <span className={clsx('inline-flex min-w-0 max-w-full items-center gap-1 align-baseline', className)}>
      <code title={value} className="truncate font-mono text-[12.5px] tracking-tight text-ink">
        {length && value.length > length && isUuid(value) ? '…' : ''}
        {shown}
        {length && value.length > length && !isUuid(value) ? '…' : ''}
      </code>
      {copy && (
        <button
          type="button"
          aria-label={`Copy ${label ?? 'value'}`}
          // 28px visual, 36px hit area (the ::before extends 4px each side); negative margin keeps the line height.
          className="relative -my-[6px] inline-flex size-[28px] shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors before:absolute before:-inset-[4px] before:content-[''] hover:bg-surface-2 hover:text-ink"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            });
          }}
        >
          {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
        </button>
      )}
    </span>
  );
}

export interface CountdownProps {
  /** ISO time the countdown ends. */
  until: string;
  /** Override "now" for tests. */
  now?: () => number;
  className?: string;
}

/** Live countdown (`12m 03s`) with `role="timer"`; turns into `expired`. Updates every second. */
export function Countdown({ until, now = Date.now, className }: CountdownProps) {
  const end = new Date(until).getTime();
  const [remaining, setRemaining] = useState(() => end - now());
  useEffect(() => {
    setRemaining(end - now());
    const timer = setInterval(() => setRemaining(end - now()), 1000);
    return () => clearInterval(timer);
  }, [end, now]);
  const expired = remaining <= 0;
  const urgent = !expired && remaining < 5 * 60_000;
  return (
    <span
      role="timer"
      aria-live="off"
      aria-label={expired ? 'expired' : `${formatCountdown(remaining)} remaining`}
      className={clsx(
        'font-mono text-[12.5px]',
        expired ? 'text-bad' : urgent ? 'text-accent-ink' : 'text-ink',
        className,
      )}
    >
      {formatCountdown(remaining)}
    </span>
  );
}

type Json = unknown;

/** Flattens nested objects into dot paths: `{a:{b:1}}` → `{'a.b': 1}`. Arrays are kept as values. */
export function flattenRecord(
  value: Record<string, Json> | null | undefined,
  prefix = '',
): Record<string, Json> {
  const out: Record<string, Json> = {};
  if (!value) return out;
  for (const [key, v] of Object.entries(value)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flattenRecord(v as Record<string, Json>, path));
    } else {
      out[path] = v;
    }
  }
  return out;
}

export type DiffKind = 'added' | 'removed' | 'changed' | 'unchanged';

export interface DiffRow {
  path: string;
  before: Json;
  after: Json;
  kind: DiffKind;
}

/** Field-level diff of two records (flattened). Unchanged rows are included, sorted changed first. */
export function diffRecords(
  before: Record<string, Json> | null | undefined,
  after: Record<string, Json> | null | undefined,
): DiffRow[] {
  const a = flattenRecord(before);
  const b = flattenRecord(after);
  const paths = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  const rows = paths.map<DiffRow>((path) => {
    const inA = Object.hasOwn(a, path);
    const inB = Object.hasOwn(b, path);
    const kind: DiffKind = !inA
      ? 'added'
      : !inB
        ? 'removed'
        : JSON.stringify(a[path]) === JSON.stringify(b[path])
          ? 'unchanged'
          : 'changed';
    return { path, before: a[path], after: b[path], kind };
  });
  const order: Record<DiffKind, number> = { changed: 0, added: 1, removed: 2, unchanged: 3 };
  return rows.sort((x, y) => order[x.kind] - order[y.kind] || x.path.localeCompare(y.path));
}

function renderValue(v: Json): ReactNode {
  if (v === undefined) return <span className="text-ink-3">—</span>;
  if (v === null) return <span className="font-mono text-ink-3">null</span>;
  if (typeof v === 'string') return v;
  return <code className="font-mono text-[12px]">{JSON.stringify(v)}</code>;
}

const KIND_LABEL: Record<DiffKind, string> = {
  added: 'added',
  removed: 'removed',
  changed: 'changed',
  unchanged: '',
};

export interface DiffTableProps {
  before: Record<string, Json> | null;
  after: Record<string, Json> | null;
  caption?: string;
  /** Hide unchanged fields (default false: approvers see the whole record). */
  changesOnly?: boolean;
  /**
   * Phone layout (default false): one block per field with the field name, the before value dimmed and the new value
   * strong under it, values wrapping instead of scrolling sideways.
   */
  stacked?: boolean;
  /** Heading of the new-value column (default "After"; the approval page says "Proposed"). */
  afterLabel?: string;
}

function beforeClass(kind: DiffKind): string {
  return clsx(
    kind === 'changed' || kind === 'removed' ? 'text-bad line-through decoration-bad/40' : 'text-ink-2',
  );
}

function afterClass(kind: DiffKind): string {
  return clsx(kind === 'changed' || kind === 'added' ? 'bg-ok-soft/60 font-medium text-ink' : 'text-ink-2');
}

/** Before/after table for a change preview. New records show only `after`; deletions only `before`. */
export function DiffTable({
  before,
  after,
  caption,
  changesOnly = false,
  stacked = false,
  afterLabel = 'After',
}: DiffTableProps) {
  const rows = diffRecords(before, after).filter((r) => !changesOnly || r.kind !== 'unchanged');
  if (stacked) {
    return (
      <ul aria-label={caption} data-stacked className="flex flex-col divide-y divide-line text-[13px]">
        {rows.map((row) => (
          <li key={row.path} data-kind={row.kind} className="flex min-w-0 flex-col gap-1 px-3 py-2.5">
            <span className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-mono text-[12px] break-all text-ink-2">{row.path}</span>
              {KIND_LABEL[row.kind] && (
                <span className="font-mono text-[12px] text-ink-3">{KIND_LABEL[row.kind]}</span>
              )}
            </span>
            {row.kind !== 'added' && (
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="w-16 shrink-0 text-[12px] text-ink-3">Before</span>
                <span className={clsx('min-w-0 break-words opacity-80', beforeClass(row.kind))}>
                  {renderValue(row.before)}
                </span>
              </span>
            )}
            {row.kind !== 'removed' && (
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="w-16 shrink-0 text-[12px] text-ink-3">{afterLabel}</span>
                <span
                  className={clsx(
                    'min-w-0 rounded px-1 break-words',
                    row.kind === 'unchanged' ? 'text-ink-2' : 'font-semibold text-ink',
                    (row.kind === 'changed' || row.kind === 'added') && 'bg-ok-soft/60',
                  )}
                >
                  {renderValue(row.after)}
                </span>
              </span>
            )}
          </li>
        ))}
        {rows.length === 0 && <li className="px-3 py-2.5 text-ink-3">No field changes.</li>}
      </ul>
    );
  }
  return (
    <div className="w-full overflow-x-auto">
      <table className="wb-table w-full border-collapse text-left text-[13px]">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Before</th>
            <th scope="col">{afterLabel}</th>
            <th scope="col">
              <span className="sr-only">Change</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.path} data-kind={row.kind}>
              <td className="font-mono text-[12px] text-ink-2">{row.path}</td>
              <td className={beforeClass(row.kind)}>{renderValue(row.before)}</td>
              <td className={afterClass(row.kind)}>{renderValue(row.after)}</td>
              <td className="w-px whitespace-nowrap text-right font-mono text-[12px] text-ink-3">
                {KIND_LABEL[row.kind]}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="text-ink-3">
                No field changes.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Recursive read-only view of structured output: objects as definition lists, arrays as lists. */
export function JsonView({ value, depth = 0 }: { value: Json; depth?: number }) {
  if (value === null || value === undefined || typeof value !== 'object') {
    return <>{renderValue(value)}</>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-ink-3">none</span>;
    const allScalar = value.every((v) => v === null || typeof v !== 'object');
    if (allScalar)
      return <span>{value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ')}</span>;
    return (
      <ol className="flex flex-col gap-2">
        {value.map((v, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional data
          <li key={i} className="rounded-xl border border-line bg-surface-2 p-3">
            <JsonView value={v} depth={depth + 1} />
          </li>
        ))}
      </ol>
    );
  }
  const entries = Object.entries(value as Record<string, Json>);
  if (entries.length === 0) return <span className="text-ink-3">empty</span>;
  return (
    <dl
      className={clsx(
        'grid grid-cols-[minmax(8rem,max-content)_minmax(0,1fr)] gap-x-4 gap-y-1.5',
        depth > 0 && 'text-[12.5px]',
      )}
    >
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-ink-3">{humanize(k)}</dt>
          <dd className="min-w-0 break-words text-ink">
            <JsonView value={v} depth={depth + 1} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
